import type { Asset } from './types';

export type ImagePreviewActionId = 'set-profile-picture' | 'edit' | 'download' | 'delete';

// PatientFileTypes the backend accepts as an avatar source; it rejects any other with a 400.
export const PROFILE_PICTURE_SOURCE_TYPES: readonly string[] = [
    'PHOTO',
    'ADDITIONAL',
    'CHAT_ATTACHMENT'
];

export interface ImagePreviewActionRuleInput {
    asset: Asset;
    showPreviewActions: boolean;
    canDelete: boolean;
    canRename: boolean;
    canSetProfilePicture: boolean;
    hasSetProfilePictureApi: boolean;
}

const isMutableGcsFile = (asset: Asset): boolean =>
    asset.source === 'gcs' && Boolean(asset.patientFileId);

const isImageMimeType = (asset: Asset): boolean =>
    asset.fileType.toLowerCase().startsWith('image/');

const isProfilePictureSource = (asset: Asset): boolean =>
    Boolean(asset.patientFileId) &&
    PROFILE_PICTURE_SOURCE_TYPES.includes(asset.storageCategory ?? '') &&
    isImageMimeType(asset) &&
    asset.internal !== true;

const ACTION_RULES: Record<ImagePreviewActionId, (input: ImagePreviewActionRuleInput) => boolean> =
    {
        'set-profile-picture': ({ asset, canSetProfilePicture, hasSetProfilePictureApi }) =>
            canSetProfilePicture && hasSetProfilePictureApi && isProfilePictureSource(asset),
        edit: ({ asset, canRename }) => canRename && isMutableGcsFile(asset),
        download: () => true,
        delete: ({ asset, canDelete }) => canDelete && isMutableGcsFile(asset)
    };

// Menu order, top to bottom.
const ACTION_ORDER: readonly ImagePreviewActionId[] = [
    'set-profile-picture',
    'edit',
    'download',
    'delete'
];

/** Empty when the host hasn't opted in, so the toolbar shows no menu button at all. */
export const resolveImagePreviewActionIds = (
    input: ImagePreviewActionRuleInput
): ImagePreviewActionId[] => {
    if (!input.showPreviewActions) return [];
    return ACTION_ORDER.filter((id) => ACTION_RULES[id](input));
};
